import { z } from 'zod';
import { openWorldCommandIdSchema } from './crossing';

/**
 * **As dezasseis cartas, no fio de um quadrante** (gate V28I.P).
 *
 * ## O que o cliente pode dizer, e o que ele não pode
 *
 * Ele diz **qual espaço**, e mais nada. Não há campo para escolher o dono — ele
 * vem do socket —, não há campo para nível, arrefecimento, alvo, duração de
 * carga, dano nem resultado. Nenhuma dessas coisas é recusada por uma
 * verificação: elas não têm por onde entrar, que é uma garantia mais forte.
 *
 * ## Porque três intenções e não uma com um modo
 *
 * Porque cada uma tem um tempo de vida diferente. Ligar e soltar são duas
 * metades de um gesto que pode durar quinze segundos, e pilotar não é sequer
 * uma carta — é uma nave a encostar-se a uma estrutura que outra pessoa pôs no
 * mundo. Uma intenção com um campo `modo` seria três coisas a partilhar uma
 * forma, e o dia em que uma delas ganhasse um campo as outras ganhavam-no
 * também.
 *
 * ## Porque todas levam identidade
 *
 * Porque **repetir tem de LER em vez de obedecer**. Um cliente honesto que não
 * viu a carta acender volta a pedir, e sem identidade o servidor ligaria a
 * carta duas vezes — ou, pior, gastaria dois arrefecimentos. A sala guarda as
 * últimas identidades por casco, com teto, e uma que ela reconheça devolve a
 * verdade em vez de uma segunda ativação.
 */

/** O espaço: 1, 2 ou 3, pela ordem em que a barra desenha. */
export const openWorldCardSlotSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

export type OpenWorldCardSlotNumber = z.infer<typeof openWorldCardSlotSchema>;

/**
 * **Ligar a carta deste espaço.**
 *
 * Para as duas que carregam, isto começa a carga: o servidor regista o tique em
 * que a pressão começou e conta pelo relógio DELE. O cliente nunca informa
 * "carreguei doze segundos" — não há campo para isso, e é essa ausência que
 * torna a carga incontestável.
 */
export const openWorldCardActivateSchema = z
  .object({
    t: z.literal('ka'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    s: openWorldCardSlotSchema,
  })
  .strict();

/**
 * **Soltar a carga deste espaço.**
 *
 * A segunda metade do gesto. Uma soltura que não encontre carga nenhuma é
 * recusada em silêncio: soltar uma carta que não estava a carregar não dispara
 * nada, e não gasta nada.
 */
export const openWorldCardReleaseSchema = z
  .object({
    t: z.literal('kr'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    s: openWorldCardSlotSchema,
  })
  .strict();

/**
 * **Pedir para pilotar um Leviatã.**
 *
 * Não nomeia a estrutura, e é deliberado: o servidor relê a posição do casco e
 * escolhe a que está ao alcance, pela ordem canónica. Um pedido feito longe é
 * consumido na tentativa — ele vale um tique, e não fica guardado à espera de
 * apanhar uma estrutura mais tarde.
 */
export const openWorldCardBoardSchema = z
  .object({
    t: z.literal('kb'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  O que o servidor diz                                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Os três espaços de UM casco, para o HUD dele.**
 *
 * ## Porque é privado
 *
 * Porque um arrefecimento é informação de DECISÃO. Que os canhões estão
 * montados é público — eles estão no ecrã a disparar —, mas quanto falta para
 * poderem voltar é uma coisa que quem está do outro lado não tem como ver, e
 * mandar-lha seria dar-lhe uma vantagem que o jogo não decidiu dar.
 *
 * ## Porque três, sempre
 *
 * Porque a barra desenha três caixas. Um espaço vazio diz-se com `d: null`, e
 * uma lista que encolhesse obrigaria o cliente a adivinhar quais faltam.
 *
 * O que NÃO viaja: o identificador da CÓPIA que a conta possui. Ele é posse, e
 * um quadrante não tem nada que ver com o cofre.
 */
export const openWorldCardSlotFrameSchema = z
  .object({
    s: openWorldCardSlotSchema,
    /** A carta congelada neste espaço, ou `null` se ele está vazio. */
    d: z.string().min(1).max(128).nullable(),
    /** Milissegundos que a habilidade ainda tem. Zero = inativa. */
    a: z.number().int().min(0),
    /** Milissegundos de arrefecimento que faltam. Zero = pronta. */
    c: z.number().int().min(0),
    /** O arrefecimento TOTAL desta carta, para o anel ter denominador. */
    ct: z.number().int().min(0),
    /** O nível, que a barra mostra. Meu, e nunca de outra pessoa. */
    l: z.number().int().min(1).max(6).optional(),
  })
  .strict();

export type OpenWorldCardSlotFrame = z.infer<typeof openWorldCardSlotFrameSchema>;

/**
 * **A metade privada das cartas de uma ligação.**
 *
 * Os três espaços, e a fração da carga em curso. Vai só a quem a pediu, e o
 * adversário nunca a vê.
 */
export const openWorldCardHudSchema = z
  .object({
    t: z.literal('kc'),
    k: z.array(openWorldCardSlotFrameSchema).length(3),
  })
  .strict();

export type OpenWorldCardHud = z.infer<typeof openWorldCardHudSchema>;

/**
 * **Uma invocação, no fio.**
 *
 * O Drone e o Leviatã. O que é, onde está, para onde aponta, de quem é, e
 * quanto da vida dela resta. **Não viaja** o tempo que lhe falta — isso é
 * informação de decisão de quem a invocou — nem o acumulador de disparo.
 *
 * O piloto viaja: a cena tem de saber que aquele casco está lá dentro para não
 * o desenhar ao lado.
 */
export const openWorldSummonEntrySchema = z
  .object({
    /** A entidade pública desta invocação, do mesmo contador dos cascos. */
    i: z.number().int().min(1),
    /** `d` de drone, `l` de leviatã. Uma letra: há duas formas, e não mais. */
    k: z.union([z.literal('d'), z.literal('l')]),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    ax: z.number().int(),
    ay: z.number().int(),
    /** A entidade de quem a invocou. A cena pinta-a com a cor dele. */
    o: z.number().int().min(1),
    /** A vida que resta, em basis points do máximo dela. */
    hp: z.number().int().min(0).max(10_000),
    /** Quem a pilota, ou ausente. Só o Leviatã. */
    pl: z.number().int().min(1).optional(),
    /**
     * O raio do CORPO, em milli-unidades, para a cena a desenhar do tamanho
     * certo.
     *
     * Ele depende da vida máxima de quem a invocou — "escala pela vida" é uma
     * decisão aprovada — e a vida máxima vive no perfil congelado, que nunca sai
     * do servidor. O que viaja é o FACTO, e nunca a razão.
     */
    r: z.number().int().min(1),
  })
  .strict();

export type OpenWorldSummonEntry = z.infer<typeof openWorldSummonEntrySchema>;

/**
 * **As bandeiras públicas que as cartas produzem sobre um corpo.**
 *
 * As mesmas letras do duelo, e é deliberado: a cena que as desenha é a mesma
 * família, e duas grafias para o mesmo estado seriam duas oportunidades de uma
 * delas ficar por desenhar.
 *
 * Ausentes quando não há nada, que é o estado de quase todo o corpo em quase
 * todo o tique: um zero explícito custaria bytes em todas as tramas de todos os
 * quadrantes para dizer o normal.
 */
export const openWorldCardFlagsSchema = z.object({
  /** Congelado por uma carta. */
  fz: z.literal(1).optional(),
  /** Inalvejável: os tiros atravessam-no. */
  bt: z.literal(1).optional(),
  /** Infetado com Malware: a cura que receber vale menos. */
  mw: z.literal(1).optional(),
  /** Paralisia TOTAL: este congelamento também trava o ataque. */
  pz: z.literal(1).optional(),
  /** Lento. */
  lt: z.literal(1).optional(),
  /** Quanta casca de gelo desenhar, de um a três. */
  ft: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  /** Em curto-circuito: a cadência dele está reduzida. */
  cc: z.literal(1).optional(),
  /** Marcado pela Caveira: o dano que causa vale menos. */
  sk: z.literal(1).optional(),
});

/**
 * **O que as cartas deste casco estão a DESENHAR.**
 *
 * Uma lista de letras, e não uma bandeira por carta: as visíveis já são várias
 * e vão ser mais, e um campo por cada uma seria uma subida de protocolo por
 * habilidade escrita. As letras são as do duelo, pela mesma razão que as
 * bandeiras são.
 */
export const openWorldCardVisualSchema = z.array(
  z.union([
    /** O círculo mágico passivo do Turbilhão: ele está EQUIPADO. */
    z.literal('o'),
    /** A lâmina do Turbilhão está a cortar. */
    z.literal('b'),
    /** O feixe da Lança Solar está a arder. */
    z.literal('l'),
    /** A Lança Solar está a carregar. */
    z.literal('c'),
    /** A Esfera Ígnea está a carregar, e a bola cresce na boca da nave. */
    z.literal('g'),
    /** O Arranque está a avançar. */
    z.literal('d'),
    /** A Sobremarcha está ligada. */
    z.literal('h'),
    /** O Manto Espectral está ligado. Só chega a quem já via este casco. */
    z.literal('k'),
    /** O Malware acabou de ser lançado DESTE casco. */
    z.literal('w'),
    /** A Marca da Caveira acabou de sair deste casco. */
    z.literal('m'),
  ]),
);

export type OpenWorldCardVisual = z.infer<typeof openWorldCardVisualSchema>;

/**
 * **Uma explosão de CARTA que acabou de acontecer** (gate V28I.P.1).
 *
 * ## Porque ela não viaja no canal das joias
 *
 * Porque não é uma joia. O canal das joias é uma lista fechada de sete
 * efeitos, e uma explosão de carta publicada como `atomic_blast` seria a
 * Esfera Ígnea e o Catolé a leem-se como um Núcleo Atómico — e a cena escolhe
 * a arte pelo ID e nunca pelo tamanho, portanto ela não teria como as
 * distinguir. O duelo já lhes dá identidades próprias; este canal é o
 * equivalente deste mundo.
 *
 * ## Porque ela não é derivável
 *
 * Um corpo que desaparece da vista não rebentou necessariamente: ele pode ter
 * expirado, ter saído da área de interesse, ou ter-se ido embora com o dono
 * numa travessia. E o CENTRO de uma explosão que aterra num casco é a posição
 * do casco, e não a última posição publicada do corpo. Nada disto se deduz de
 * duas tramas seguidas.
 *
 * ## O que ela nunca carrega
 *
 * Dano, dono, carta, nível, ou quem foi apanhado. O raio é o MECÂNICO — o
 * desenho abre exatamente até onde ele feriu — e é a única medida que sai.
 */
export const openWorldCardBurstSchema = z
  .object({
    /** O id do corpo que rebentou. Deduplicável, e nunca uma posição na lista. */
    i: z.number().int().min(1),
    /** `f` a Esfera Ígnea, `c` o Catolé. Só os dois corpos que rebentam. */
    k: z.union([z.literal('f'), z.literal('c')]),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** O raio MECÂNICO da explosão, em milli-unidades. */
    r: z.number().int().min(0),
  })
  .strict();

export type OpenWorldCardBurst = z.infer<typeof openWorldCardBurstSchema>;
